import React, { useMemo, useRef, useState } from 'react'
import { Button, Card, Col, Modal, Row, Spinner } from 'react-bootstrap'
import { BsCheckCircleFill, BsEye, BsXLg } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import axios from 'axios'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'

import { BASE_URL } from './commonComponents/ApiEndpoints'
import { useSidebar } from './SidebarContext'
import { useAuth } from './AuthContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'

import CertificateTemplate1 from '../certificateTemplates/CertificateTemplate1'
import CertificateTemplate2 from '../certificateTemplates/CertificateTemplate2'
import CertificateTemplate3 from '../certificateTemplates/CertificateTemplate3'
import CertificateTemplate4 from '../certificateTemplates/CertificateTemplate4'
import CertificateTemplate5 from '../certificateTemplates/CertificateTemplate5'
import CertificateTemplate6 from '../certificateTemplates/CertificateTemplate6'
import CertificateTemplate7 from '../certificateTemplates/CertificateTemplate7'
import CertificateTemplate8 from '../certificateTemplates/CertificateTemplate8'
import { BsFiles } from 'react-icons/bs'

import CertificateAchievementPrint, {
  buildCertificateAchievementPreviewData,
} from './CertificateAchievementPrint'
import TransferCertificatePrint from './TransferCertificatePrint'
import ExamMarksheetPrint from './ExamMarksheetPrint'

const DEFAULT_TEMPLATE_TYPE = 'none'

const templateRegistry = [
  { id: '1', title: 'Achievement/Merit Certificate', type: 'achievement_merit_template', component: CertificateTemplate1 },
  { id: '2', title: 'Transfer Certificate', type: 'transfer_certificate_template', component: CertificateTemplate2 },
  { id: '3', title: 'Character Certificate', type: 'character_certificate_template', component: CertificateTemplate3 },
  { id: '4', title: 'Vocational/Skill Certificate', type: 'vocational_skill_template', component: CertificateTemplate4 },
  { id: '5', title: 'Passing Certificate', type: 'passing_certificate_template', component: CertificateTemplate5 },
  { id: '6', title: 'Migration Certificate', type: 'migration_certificate_template', component: CertificateTemplate6 },
  { id: '7', title: 'Cultural Activity Certificate', type: 'cultural_activity_template', component: CertificateTemplate7 },
  { id: '8', title: 'Improvement Certificate', type: 'improvement_certificate_template', component: CertificateTemplate8 },
]

const customTemplateRegistry = [
  { id: '9', title: 'Course Completion Certificate', type: 'course_completion_certificate' },
  { id: '10', title: 'Transfer Certificate', type: 'transfer_certificate' },
  { id: '11', title: 'Exam Marksheet', type: 'exam_marksheet' },
]

const getStorageKey = (instituteId) => `assigned_template_${instituteId}`

const findTemplateById = (templateId) => {
  if (templateId === null || templateId === undefined || templateId === '') return null
  const sid = String(templateId)
  return templateRegistry.find((tpl) => tpl.id === sid) || customTemplateRegistry.find((tpl) => tpl.id === sid) || null
}

const getTemplateTypeById = (templateId) =>
  findTemplateById(templateId)?.type || DEFAULT_TEMPLATE_TYPE

const getTemplateIdByType = (templateType) =>
  customTemplateRegistry.find((tpl) => tpl.type === templateType)?.id ||
  templateRegistry.find((tpl) => tpl.type === templateType)?.id ||
  null

const resolveTemplateIdFromApiRow = (row) =>
  getTemplateIdByType(row?.template_type) ||
  normalizeTemplateId(row?.template_id) ||
  normalizeTemplateId(row?.id) ||
  (row?.selected === 0 || row?.selected === 1 ? null : normalizeTemplateId(row?.selected))

const normalizeTemplateId = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    if (trimmed === '9' || trimmed === '10' || trimmed === '11') return trimmed
    if (trimmed.startsWith('template')) {
      const numericPart = Number(trimmed.replace('template', ''))
      if (!Number.isNaN(numericPart) && numericPart >= 1 && numericPart <= templateRegistry.length) {
        return String(numericPart)
      }
      return null
    }
    const numeric = Number(trimmed)
    if (!Number.isNaN(numeric)) {
      if (numeric === 9 || numeric === 10 || numeric === 11) return String(numeric)
      if (numeric >= 1 && numeric <= templateRegistry.length) return String(numeric)
      if (numeric >= 0 && numeric < templateRegistry.length) return String(numeric + 1)
    }
    return null
  }

  if (typeof value === 'number') {
    if (value === 9 || value === 10 || value === 11) return String(value)
    if (value >= 1 && value <= templateRegistry.length) return String(value)
    if (value >= 0 && value < templateRegistry.length) return String(value + 1)
  }

  return null
}

const TemplateAssignment = () => {
  const { isCollapsed } = useSidebar()
  const { logout } = useAuth()
  const { logo, name: instituteName } = useInstitute()
  const [logoError, setLogoError] = useState(false)
  const [previewTemplateId, setPreviewTemplateId] = useState(null)
  const [previewCustomTemplateId, setPreviewCustomTemplateId] = useState(null)
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([])
  const [fetchedTemplateIds, setFetchedTemplateIds] = useState([])
  const [draftSelectedIds, setDraftSelectedIds] = useState([])
  const [assigningTemplateId, setAssigningTemplateId] = useState('')
  const [confirmAssignTemplateId, setConfirmAssignTemplateId] = useState('')
  const [showTemplates, setShowTemplates] = useState(false)
  const templatesSectionRef = useRef(null)
  const [marginLeft, setMarginLeft] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 992
      ? isCollapsed
        ? '81px'
        : '261px'
      : '0px',
  )
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )

  const instituteId = Cookies.get('institute_id')
  const token = useMemo(() => {
    const userCookie = Cookies.get('user')
    try {
      return userCookie ? JSON.parse(userCookie)?.token : null
    } catch {
      return null
    }
  }, [])

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setMarginLeft(isCollapsed ? '81px' : '261px')
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const loadAssignedFromStorage = React.useCallback(() => {
    if (!instituteId) return []
    const key = getStorageKey(instituteId)
    const storedTemplateId = localStorage.getItem(key)
    if (!storedTemplateId) return []
    try {
      const parsed = JSON.parse(storedTemplateId)
      if (Array.isArray(parsed)) return parsed.map((id) => normalizeTemplateId(id)).filter(Boolean).slice(0, 1)
      if (parsed) return [normalizeTemplateId(parsed)].filter(Boolean)
    } catch {
      return [normalizeTemplateId(storedTemplateId)].filter(Boolean)
    }
    return []
  }, [instituteId])

  const fetchAssignedTemplates = React.useCallback(async (preferredTemplateType) => {
    if (!instituteId || !token) return null
    try {
      const response = await axios.get(`${BASE_URL}/template`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'institute-id': Number(instituteId),
        },
      })
      const rawData = response?.data?.data
      const rows = Array.isArray(rawData)
        ? rawData
        : rawData && typeof rawData === 'object'
          ? [rawData]
          : Array.isArray(response?.data)
            ? response.data
            : []
      
      const availableIds = Array.from(
        new Set(rows.map((row) => resolveTemplateIdFromApiRow(row)).filter(Boolean)),
      )

      const matchingRow = preferredTemplateType
        ? rows.find(
            (item) =>
              item?.template_type === preferredTemplateType &&
              (item?.selected === 1 ||
                item?.selected === '1' ||
                item?.selected === true ||
                item?.selected === 'true'),
          )
        : null
      const selectedRow =
        matchingRow ||
        rows.find(
          (item) =>
            item?.selected === 1 ||
            item?.selected === '1' ||
            item?.selected === true ||
            item?.selected === 'true',
        ) ||
        null
      const resolvedSelectedId = resolveTemplateIdFromApiRow(selectedRow)
      const assignedIds = resolvedSelectedId ? [resolvedSelectedId] : []

      return { assignedIds, availableIds }
    } catch {
      return null
    }
  }, [instituteId, token])

  React.useEffect(() => {
    if (!instituteId) return
    const localIds = loadAssignedFromStorage().slice(0, 1)
    setSelectedTemplateIds(localIds)
    setDraftSelectedIds(localIds)

    const syncAssignedTemplates = async () => {
      const apiData = await fetchAssignedTemplates()
      if (!apiData) return
      setFetchedTemplateIds(apiData.availableIds)
      setSelectedTemplateIds(apiData.assignedIds)
      setDraftSelectedIds(apiData.assignedIds)
      localStorage.setItem(getStorageKey(instituteId), JSON.stringify(apiData.assignedIds))
    }
    syncAssignedTemplates()
  }, [instituteId, loadAssignedFromStorage, fetchAssignedTemplates])

  const previewTemplate = templateRegistry.find((tpl) => tpl.id === previewTemplateId)
  const PreviewComponent = previewTemplate?.component
  const previewCustomTemplate = customTemplateRegistry.find((tpl) => tpl.id === previewCustomTemplateId)
  const isDraftAssignConfirm = confirmAssignTemplateId === '__draft__'
  const draftSelectedTemplateTitle =
    findTemplateById(draftSelectedIds[0])?.title || ''
  const listedTemplateIds = selectedTemplateIds.length ? selectedTemplateIds : fetchedTemplateIds
  const assignableTemplateIds = fetchedTemplateIds.length
    ? fetchedTemplateIds
    : templateRegistry.map((tpl) => tpl.id)

  const draftCount = draftSelectedIds.length
  const selectionSummary =
    draftCount === 1 ? 'Select Your Template' : `${draftCount} Templates are selected`

  const handleAssignTemplate = async (templateIdOrIds) => {
    if (!instituteId) {
      toast.error('Institute not selected. Please select institute first.')
      return
    }
    if (!token) {
      toast.error('Authorization token missing. Please login again.')
      return
    }

    const templateIds = (Array.isArray(templateIdOrIds)
      ? Array.from(new Set(templateIdOrIds.filter(Boolean)))
      : [templateIdOrIds].filter(Boolean)
    ).slice(0, 1)

    const template_id =
      templateIds.length === 0 ? [] : templateIds.length === 1 ? templateIds[0] : templateIds
    const templateType = getTemplateTypeById(templateIds[0])

    try {
      setAssigningTemplateId(templateIds[0] || '__clear__')
      await axios.post(
        `${BASE_URL}/template/assign`,
        {
          template_type: templateType,
          institute_id: Number(instituteId),
          template_id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      )
      const next = templateIds.length ? templateIds : []
      setSelectedTemplateIds(next)
      setDraftSelectedIds(next)
      localStorage.setItem(getStorageKey(instituteId), JSON.stringify(next))
      const apiData = await fetchAssignedTemplates(templateType)
      if (apiData) {
        setFetchedTemplateIds(apiData.availableIds)
        setSelectedTemplateIds(apiData.assignedIds)
        setDraftSelectedIds(apiData.assignedIds)
        localStorage.setItem(getStorageKey(instituteId), JSON.stringify(apiData.assignedIds))
      }
      toast.success(
        templateIds.length ? 'Template assigned successfully' : 'All templates have been unassigned',
      )
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.result ||
        'Failed to assign template'
      toast.error(msg)
    } finally {
      setAssigningTemplateId('')
    }
  }

  return (
    <>
      <style>
        {`
          .template-assignment-grid .card {
            transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
          }
          .template-assignment-grid .card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08);
          }
          .template-preview-wrap [class*="actions"] {
            display: none !important;
          }
          .template-preview-wrap {
            max-height: 75vh;
            overflow: auto;
            background: #f8fafc;
            border-radius: 10px;
            padding: 10px;
          }
          /* Certificate.js layout uses fixed SVG offsets; needs ~full page width to match /Certificate */
          .template-preview-wrap .certificate-printable {
            min-width: 960px;
            box-sizing: border-box;
          }
          .template-preview-wrap .transfer-cert-print-wrap {
            min-width: 210mm;
            box-sizing: border-box;
          }
          .template-preview-wrap .exam-marksheet-print-root {
            min-width: 900px;
            box-sizing: border-box;
          }
        `}
      </style>

      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{ marginTop: typeof window !== 'undefined' && window.innerWidth < 992 ? '55px' : '-12px' }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                padding: '12px 28px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div>
                  <h3 className="m-0 fw-bold text-start" style={{ color: '#223843' }}>
                    Manage Templates
                  </h3>
                  <p className="m-0 text-start" style={{ color: '#6b7280', fontSize: '13px' }}>
                    Preview and assign one active template for this institute.
                  </p>
                </div>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div className="container-fluid mt-4" style={{ padding: '0 32px 24px' }}>
          <div
            className={`mb-4 d-flex gap-2 ${
              isMobile
                ? 'flex-column align-items-stretch'
                : 'align-items-center justify-content-between'
            }`}
          >
            <div
              className={`d-flex align-items-center gap-2 ${
                isMobile ? 'flex-nowrap w-100' : 'flex-wrap'
              }`}
            >
              <button
                type="button"
                className="d-flex align-items-center justify-content-center"
                onClick={() => {
                  setShowTemplates((prev) => {
                    if (!prev) setDraftSelectedIds([...selectedTemplateIds.slice(0, 1)])
                    return true
                  })
                  requestAnimationFrame(() => {
                    templatesSectionRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    })
                  })
                }}
                style={{
                  borderRadius: '10px',
                  padding: '6px 10px',
                  height: '44px',
                  border: 'none',
                  backgroundColor: '#223843',
                  color: '#ffffff',
                  gap: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  ...(isMobile ? { flex: 1, minWidth: 0 } : {}),
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1f323a'
                  e.currentTarget.style.transform = 'scale(1.03)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title="Assign Templates"
              >
                <BsFiles size={18} />
                <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Assign Templates
                </span>
              </button>
              {showTemplates && (
                <button
                  type="button"
                  className="d-flex align-items-center justify-content-center"
                  onClick={() => {
                    setShowTemplates(false)
                    setDraftSelectedIds([...selectedTemplateIds.slice(0, 1)])
                  }}
                  style={{
                    borderRadius: '10px',
                    padding: '6px 10px',
                    height: '44px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    gap: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                    ...(isMobile ? { flex: 1, minWidth: 0 } : {}),
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#F3F4F6'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff'
                  }}
                  title="Close template list"
                  aria-label="Close template list"
                >
                  <BsXLg size={18} />
                  <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Close
                  </span>
                </button>
              )}
            </div>
            {isMobile ? (
              <>
                <div
                  className="w-100"
                  style={{
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    color: '#374151',
                  }}
                >
                  Institute: <strong>{instituteName || '-'}</strong>
                </div>
              </>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    color: '#374151',
                  }}
                >
                  Institute: <strong>{instituteName || '-'}</strong>
                </div>
              </div>
            )}
          </div>

          <Card
            className="mb-4"
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              background: '#ffffff',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '16px 18px',
                background: '#F3F4F6',
                borderBottom: '1px solid #D1D3D4',
              }}
            >
              <div className="d-flex align-items-center justify-content-between gap-2">
                <div className="text-start">
                  <h6 className="m-0 fw-bold" style={{ color: '#223843' }}>
                    Active Template
                  </h6>
                  {/* <p className="m-0 mt-1" style={{ fontSize: '12px', color: '#6b7280' }}>
                    {selectedTemplateIds.length ? (
                      <>
                        {selectedTemplateIds.length} template{selectedTemplateIds.length > 1 ? 's' : ''} assigned
                      </>
                    ) : (
                      <>No templates assigned yet</>
                    )}
                  </p> */}
                </div>
              </div>
            </div>
            <Card.Body style={{ padding: '16px 18px' }}>
              {listedTemplateIds.length ? (
                <div className="d-flex flex-wrap gap-2">
                  {listedTemplateIds
                    .map((id) => findTemplateById(id))
                    .filter(Boolean)
                    .map((tpl) => (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => {
                          const isCustom = customTemplateRegistry.some((c) => c.id === String(tpl.id))
                          if (isCustom) {
                            setPreviewTemplateId(null)
                            setPreviewCustomTemplateId(tpl.id)
                          } else {
                            setPreviewCustomTemplateId(null)
                            setPreviewTemplateId(tpl.id)
                          }
                        }}
                        style={{
                          borderRadius: '999px',
                          border: '1px solid #D1D3D4',
                          background: '#F9FAFB',
                          padding: '8px 12px',
                          fontSize: '13px',
                          color: '#111827',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.borderColor = '#223843'
                          e.currentTarget.style.color = '#ffffff'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#F9FAFB'
                          e.currentTarget.style.borderColor = '#D1D3D4'
                          e.currentTarget.style.color = '#111827'
                        }}
                        title="Preview assigned template"
                      >
                        {tpl.title}
                      </button>
                    ))}
                </div>
              ) : (
                <div className="text-start" style={{ fontSize: '13px', color: '#6b7280' }}>
                  {showTemplates
                    ? 'Select template below, then use Click to Assign. Assigned templates will appear here.'
                    : 'Click Assign Templates to preview and assign templates. Assigned templates will appear here.'}
                </div>
              )}
            </Card.Body>
          </Card>

          {showTemplates && (
            <>
              <div
                ref={templatesSectionRef}
                className="mb-3 d-flex flex-column flex-md-row flex-wrap align-items-start align-items-md-center justify-content-between gap-3"
                style={{
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  padding: '14px 16px',
                }}
              >
                <div className="d-flex flex-column gap-2">
                  {draftCount > 0 && (
                    <p className="m-0 text-start" style={{ fontSize: '13px', color: '#374151' }}>
                      {selectionSummary}
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => setConfirmAssignTemplateId('__draft__')}
                  disabled={Boolean(assigningTemplateId)}
                  style={{
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: '#223843',
                    minHeight: '44px',
                    padding: '8px 18px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {assigningTemplateId ? (
                    <span className="d-flex align-items-center gap-2">
                      <Spinner animation="border" size="sm" /> Assigning...
                    </span>
                  ) : (
                    'Click to Assign'
                  )}
                </Button>
              </div>
              <h6 className="fw-bold text-start mb-3" style={{ color: '#223843' }}>
                Default Templates
              </h6>
              <Row className="g-3 template-assignment-grid mb-4">
                {assignableTemplateIds
                  .map((id) => templateRegistry.find((tpl) => tpl.id === id))
                  .filter(Boolean)
                  .map((tpl) => {
                    const isAssigned = selectedTemplateIds.includes(tpl.id)
                    const isDraftSelected = draftSelectedIds.includes(tpl.id)
                    return (
                      <Col key={tpl.id} xs={12} md={6} lg={4} xl={3}>
                        <Card
                          style={{
                            borderRadius: '12px',
                            border: isAssigned ? '2px solid #223843' : '1px solid #D1D3D4',
                            background: '#ffffff',
                          }}
                        >
                          <Card.Body className="text-start">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="mb-1 fw-bold" style={{ color: '#111827' }}>
                                  {tpl.title}
                                </h6>
                                {/* <p className="mb-0" style={{ fontSize: '12px', color: '#6b7280' }}>
                                  Type: {tpl.type}
                                </p> */}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={isDraftSelected}
                                  disabled={Boolean(assigningTemplateId)}
                                  onChange={() => {
                                    setDraftSelectedIds((prev) => (prev.includes(tpl.id) ? [] : [tpl.id]))
                                  }}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                  title="Select template"
                                />
                                {isAssigned && <BsCheckCircleFill color="#2e" size={20} />}
                              </div>
                            </div>

                            <div className="mt-3">
                              <Button
                                variant="outline-secondary"
                                className="d-flex align-items-center gap-2"
                                style={{
                                  borderRadius: '8px',
                                  width: '100%',
                                  height: '34px',
                                  padding: '6px 10px',
                                  justifyContent: 'center',
                                }}
                                onClick={() => {
                                  setPreviewCustomTemplateId(null)
                                  setPreviewTemplateId(tpl.id)
                                }}
                              >
                                <BsEye /> Preview
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )
                  })}
              </Row>

              <h6 className="fw-bold text-start mb-3" style={{ color: '#223843' }}>
                Custom Template
              </h6>
              <Row className="g-3 template-assignment-grid">
                {customTemplateRegistry.map((tpl) => {
                  return (
                    <Col key={tpl.id} xs={12} md={6} lg={4} xl={3}>
                      <Card
                        style={{
                          borderRadius: '12px',
                          border: '1px solid #D1D3D4',
                          background: '#ffffff',
                        }}
                      >
                        <Card.Body className="text-start">
                          <div className="mb-2">
                            <h6 className="mb-0 fw-bold" style={{ color: '#111827' }}>
                              {tpl.title}
                            </h6>
                          </div>

                          <div className="mt-3">
                            <Button
                              variant="outline-secondary"
                              className="d-flex align-items-center gap-2"
                              style={{
                                borderRadius: '8px',
                                width: '100%',
                                height: '34px',
                                padding: '6px 10px',
                                justifyContent: 'center',
                              }}
                              onClick={() => {
                                setPreviewTemplateId(null)
                                setPreviewCustomTemplateId(tpl.id)
                              }}
                            >
                              <BsEye /> Preview
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  )
                })}
              </Row>
            </>
          )}
        </div>
      </div>

      <Modal
        show={Boolean(previewTemplateId || previewCustomTemplateId)}
        onHide={() => {
          setPreviewTemplateId(null)
          setPreviewCustomTemplateId(null)
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '18px' }}>
            {previewTemplate?.title || previewCustomTemplate?.title || 'Template Preview'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="template-preview-wrap">
            {PreviewComponent ? (
              <PreviewComponent
                studentName="Student Name"
                courseName="Course Name"
                date={new Date().toLocaleDateString()}
                instituteName={instituteName || 'Institute Name'}
                certificateTitle="Transfer Certificate"
                authorityName="Authorized Signatory"
                certificateId="TC-0001"
                instructorName="Principal"
                watermarkUrl="/logo.svg"
                logoUrl={logo}
                suppressSidebarOffset
              />
            ) : String(previewCustomTemplateId) === '9' ? (
              <CertificateAchievementPrint
                logo={logo}
                certificateData={buildCertificateAchievementPreviewData(instituteName)}
              />
            ) : String(previewCustomTemplateId) === '10' ? (
              <TransferCertificatePrint logo={logo} instituteName={instituteName} />
            ) : String(previewCustomTemplateId) === '11' ? (
              <ExamMarksheetPrint logo={logo} instituteName={instituteName || 'Institute Name'} />
            ) : null}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(confirmAssignTemplateId)}
        onHide={() => (assigningTemplateId ? null : setConfirmAssignTemplateId(''))}
        centered
      >
        <Modal.Header closeButton={!assigningTemplateId}>
          <Modal.Title style={{ fontSize: '18px' }}>Confirm Template Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2" style={{ color: '#111827' }}>
            {isDraftAssignConfirm && draftSelectedIds.length > 0 ? (
              <>
                Are you sure you want to assign{' '}
                <strong>{draftSelectedTemplateTitle || 'this'}</strong> template to this institute?
              </>
            ) : isDraftAssignConfirm ? (
              <>
                You have no templates selected. This will <strong>remove all</strong> template assignments for this
                institute.
              </>
            ) : (
              <>Please select at least one template to assign.</>
            )}
          </p>
          <p className="mb-0" style={{ fontSize: '13px', color: '#6b7280' }}>
            {draftSelectedIds.length > 0 ? (
              <>This will set the selected template for transfer certificates.</>
            ) : (
              <>After confirming, no templates will be assigned until you select and assign again.</>
            )}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmAssignTemplateId('')}
            disabled={Boolean(assigningTemplateId)}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!isDraftAssignConfirm) return
              const toAssign = draftSelectedIds[0] ? [draftSelectedIds[0]] : []
              await handleAssignTemplate(toAssign)
              setConfirmAssignTemplateId('')
            }}
            disabled={Boolean(assigningTemplateId) || !isDraftAssignConfirm}
            style={{ border: 'none', backgroundColor: '#223843' }}
          >
            {assigningTemplateId ? (
              <span className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" /> Assigning...
              </span>
            ) : draftSelectedIds.length === 0 ? (
              'Yes, Clear Assignments'
            ) : (
              'Yes, Assign Template'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default TemplateAssignment
